import { FC, memo, useContext, useEffect, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import Bugsnag from '@bugsnag/js';
import useModal from 'hooks/useModal';
import { Pagination, Row, SortableHeader, Table, Top } from 'components/Table';
import Checkbox from 'components/checkbox';
import { getInvitationsAction } from 'contexts/users/actions';
import UserRow, { ContainerDesktop } from 'components/usersTable/UserRow';
import SearchFilter from './SearchFilter';
import { SelectedCounts, Title } from 'components/layout/tableContentHeader/styles';
import StatusFilter from './StatusFilter';
import { Spinner } from 'components/loadings';
import { ActionModal } from 'components/modal';
import Notification from 'components/notification';
import { SessionContext } from 'contexts/session';
import {
  assignAddonLicense,
  resendUserInvites,
  setPendingInvitesAddon,
  setTeamManager,
  setUserLicense,
  setUserTeam,
  unassignAddonLicense,
} from 'components/usersTable/actions';
import useTeamUsersSearch from './useTeamUsersSearch';
import { teamUsersTableGrid } from './grid';
import Button from 'components/button';
import { CustomTableCard, SpinerContainer, TeamUsersTableHeader } from './styles';
import EmptyState from './EmptyState';
import { SetInvitesAddonSettings, SwitchLicenseActions } from 'components/usersTable/types';
import { Subcolumn } from 'components/usersTable/styles';
import Tooltip from 'components/tooltip';
import Icon from 'components/icons';
import { fromOrdering, getColumnDirection } from 'utils/sorting';
import SortTable from 'components/sortTable';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useLicenseRejection from 'hooks/useLicenseRejection';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import { LICENSE_UPDATED, LICENSE_UPDATE_FAILED } from 'constants/licenses';
import { skillDiveLicenseChangeFailed, skillDiveLicenseChanged } from 'constants/skill_dive';

export interface UsersCount {
  pending: number;
  active: number;
}

export interface UserTeamsPayload {
  team_id: string;
  is_manager: boolean;
}

export interface TeamUsersTableProps {
  team: any;
  totalUsers: number;
  refreshData: () => void;
}

export interface TeamUsersTableBulkButtonsProps {
  active: boolean;
  onRemoveUsers(): void;
  onResendInvitation(): void;
}

export const TableBulkButtons: FC<TeamUsersTableBulkButtonsProps> = ({
  active,
  onRemoveUsers,
  onResendInvitation,
}) => {
  return (
    <>
      {!active && (
        <Button $secondary onClick={onResendInvitation}>
          Resend invitation
        </Button>
      )}
      <Button $secondary onClick={onRemoveUsers}>
        Remove users from team
      </Button>
    </>
  );
};

const TeamsUsersTable: FC<TeamUsersTableProps> = ({ team, totalUsers, refreshData }) => {
  const { dashboardUsersSorting } = useFlags() || {};
  const [fetching, setFetching] = useState(false);
  const [usersTable, setUsersTable] = useState(null);
  const [selecteds, setSelecteds] = useState([]);
  const { modal, setModal } = useModal();
  const {
    updateLicensesAvailables,
    actions: { refreshAccounts, refreshSession },
  } = useContext(SessionContext);
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW);
  const notifyLicenseRejection = useLicenseRejection();
  const {
    requestData,
    handlePagination,
    handleStatusFilter,
    handleSearchboxInput,
    handleSortSelect,
    handleSortToggle,
  } = useTeamUsersSearch({ teamSlug: team.slug, sortingEnabled: dashboardUsersSorting });

  const active = requestData.active === 'true';
  // Pending gets the Skill Dive column with the Skill Dive flow, as on the Users page.
  const skillDiveColumn = active || skillDiveFlow;
  const sortState = fromOrdering(requestData.ordering);
  const columns = [
    { name: 'User', subcolumns: null, columnKey: 'user_display' },
    { name: 'Team', subcolumns: null },
    { name: 'Manager', subcolumns: null },
    { name: 'Plan license', subcolumns: null },
    skillDiveColumn && { name: 'Skill Dive', subcolumns: null },
    {
      name: active ? 'Last login' : 'Invited',
      subcolumns: null,
      tooltip: "User's last login to the platform",
      columnKey: active ? 'user__last_login' : 'invited_at',
    },
  ];
  const tableColumns = columns.filter((column): column is Exclude<typeof columns[number], false> =>
    Boolean(column)
  );
  const sortableColumns = tableColumns.filter(
    (column): column is typeof tableColumns[number] & { columnKey: string } =>
      Boolean(column.columnKey)
  );

  const { header: tableConfig, row: rowTableConfig } = teamUsersTableGrid(skillDiveColumn);

  useEffect(() => {
    getUsers();
    setSelecteds([]);
  }, [requestData]);

  useEffect(() => {
    if (totalUsers !== usersTable?.count) getUsers();
  }, [totalUsers]);

  const getUsers = async () => {
    setFetching(true);
    const getUsersMethod = getInvitationsAction;
    const usersResponse = await getUsersMethod(requestData);
    setFetching(false);
    if (usersResponse instanceof Error) {
      Bugsnag.notify(usersResponse.message);
      setUsersTable((prevState) => ({ ...prevState, error: usersResponse.message }));
    } else {
      setUsersTable({
        users: usersResponse.results,
        count: usersResponse.count,
      });
    }
  };

  const handleCheckboxSelection = (id?: string, check?: boolean) => {
    if (id) {
      const findRow = selecteds.find((rowId) => rowId === id);
      if (findRow) setSelecteds((prevState) => prevState.filter((rowId) => rowId !== id));
      else setSelecteds((prevState) => [...prevState, id]);
    } else {
      if (check) {
        setSelecteds(usersTable?.users?.map(({ id }) => id));
      } else setSelecteds([]);
    }
  };

  const handleRemoveUser = (ids: string[], name?: string[]) => {
    const confirmMsg = name
      ? `Are you sure you want to remove ${name} from this team?`
      : `Are you sure you want to remove ${ids.length} users from this team?`;
    const onAcceptRemove = async () => {
      setFetching(true);
      setModal(null);
      const userTeamsFiltered = [];
      ids.forEach((userId) => {
        const userData = usersTable.users.find(({ id }) => id === userId);
        userTeamsFiltered.push(userData.teams.filter(({ team_id }) => team_id !== team.id));
      });

      const response = await setUserTeam({ ids, team: userTeamsFiltered });
      if (response instanceof Error) {
        Notification({ text: 'Couldn’t remove user from team', type: 'error' });
      } else {
        const licenses = usersTable.users.filter(
          ({ id, license }) => ids.includes(id) && license
        ).length;
        setUsersTable((prevState) => {
          return {
            count: prevState.count - ids.length,
            users: prevState.users.filter(({ id }) => !ids.includes(id)),
          };
        });
        licenses && updateLicensesAvailables(false, true);
        Notification({ text: 'User removed from team', type: 'success' });
        refreshData();
      }
      setFetching(false);
    };
    setModal(
      <ActionModal
        title={'Remove User'}
        description={confirmMsg}
        active={true}
        handleCancel={() => setModal(null)}
        buttons={[
          { label: 'Cancel', type: 'secondary', onClick: () => setModal(null) },
          {
            label: 'Remove user from team',
            type: 'primary',
            onClick: onAcceptRemove,
          },
        ]}
      />
    );
  };

  const handleSwitchManager = async (user_invite_id: string, teamsData: UserTeamsPayload[]) => {
    setFetching(true);
    const response = await setTeamManager({
      user_invite_id: user_invite_id,
      teams: teamsData,
    });
    setFetching(false);
    if (!(response instanceof Error) && response.result) {
      getUsers();
      Notification({ text: 'Role updated', type: 'success' });
    } else {
      Notification({ text: 'Role couldn’t be updated', type: 'error' });
    }
  };

  const handleSwitchLicense = async ({ ids, checked }: SwitchLicenseActions) => {
    setFetching(true);
    const response = await setUserLicense({ ids, license: checked });
    if (!(response instanceof Error) && response.result) {
      const newContent = getNewTableRows({ ids, type: 'license', value: checked });
      updateUsersTable(newContent);
      updateLicensesAvailables(checked, true);
      getUsers();
      Notification({ text: LICENSE_UPDATED, type: NOTIFICATION_TYPES.SUCCESS });
    } else {
      resetUsersTable();
      notifyLicenseRejection(response, { fallback: LICENSE_UPDATE_FAILED, onRefresh: getUsers });
    }
    setFetching(false);
  };

  const handleResendInvitation = async (ids: string[]) => {
    setFetching(true);
    const response = await resendUserInvites({ ids });
    setFetching(false);
    if (!(response instanceof Error) && response.result) {
      Notification({ text: 'Invitation successfully resent', type: 'success' });
    } else {
      Notification({ text: 'Invitation couldn’t be updated', type: 'error' });
    }
  };

  const resetUsersTable = () => {
    const originalContent = usersTable;
    setUsersTable(null);
    setUsersTable(originalContent);
  };

  const updateUsersTable = (newUsersTable) => {
    setUsersTable((prevState) => {
      return {
        ...prevState,
        users: newUsersTable,
      };
    });
  };

  const getNewTableRows = ({ type, value, ids }) => {
    const newTableRows = usersTable.users.map((item) => {
      if (ids.includes(item.id)) {
        return {
          ...item,
          [type]: value,
        };
      } else return item;
    });
    return newTableRows;
  };

  const handleSwitchInviteAddon = async ({ ids, assign, addon }: SetInvitesAddonSettings) => {
    setFetching(true);
    const response = await setPendingInvitesAddon({ ids, assign, addon });
    setFetching(false);
    if (!(response instanceof Error) && response?.result) {
      getUsers();
      refreshSession();
      refreshAccounts();
      Notification({ text: skillDiveLicenseChanged(assign), type: NOTIFICATION_TYPES.SUCCESS });
    } else {
      notifyLicenseRejection(response, {
        fallback: skillDiveLicenseChangeFailed(assign),
        onRefresh: getUsers,
      });
    }
  };

  const handleSwitchAddon = async (
    assigned_to?: number | string,
    addon?: string,
    license_addon_id?: string
  ) => {
    setFetching(true);
    const is_assigning = !license_addon_id;
    let response = null;
    if (is_assigning) {
      response = await assignAddonLicense({ assigned_to, addon });
    } else {
      response = await unassignAddonLicense({ license_addon_id });
    }
    setFetching(false);
    if (!(response instanceof Error) && response) {
      Notification({
        text: skillDiveLicenseChanged(is_assigning),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
      getUsers();
    } else {
      notifyLicenseRejection(response, {
        fallback: skillDiveLicenseChangeFailed(is_assigning),
        onRefresh: getUsers,
      });
    }
  };

  return (
    <CustomTableCard>
      <TeamUsersTableHeader>
        <div className="d-md-block">
          {active ? (
            <Title className="mb-0">Active Users</Title>
          ) : (
            <Title className="mb-0">Pending Users</Title>
          )}
          <SelectedCounts className="text-muted d-md-block mb-0">
            {selecteds.length} users selected
          </SelectedCounts>
        </div>
        <SearchFilter onSearchSubmit={handleSearchboxInput} />
        {isDesktop && selecteds.length > 0 && (
          <TableBulkButtons
            active={active}
            onRemoveUsers={() => handleRemoveUser(selecteds)}
            onResendInvitation={() => handleResendInvitation(selecteds)}
          />
        )}
      </TeamUsersTableHeader>
      {!isDesktop && selecteds.length > 0 && (
        <TableBulkButtons
          active={active}
          onRemoveUsers={() => handleRemoveUser(selecteds)}
          onResendInvitation={() => handleResendInvitation(selecteds)}
        />
      )}
      <StatusFilter active={requestData?.active === 'true'} onStatusChange={handleStatusFilter} />

      {!usersTable ? (
        <SpinerContainer>
          <Spinner active top={'50%'} />
        </SpinerContainer>
      ) : (
        <Table>
          <Row columns={tableConfig} transparent={isMobile} topSelect={isMobile} top={true} sticky>
            <Top
              topSelect={isMobile}
              style={!isMobile ? { justifyContent: 'flex-start', paddingLeft: 0 } : {}}
            >
              <Checkbox
                checked={false}
                indeterminate={false}
                onChange={({ target }) => handleCheckboxSelection(null, target.checked)}
                label={null}
                darkBackground={isMobile}
                id="allTeamSelected"
              />
              {isMobile && dashboardUsersSorting && (
                <SortTable
                  title="Sort by"
                  name="ordering"
                  position="left"
                  items={[
                    { label: 'Clear sort', value: '', selected: !requestData.ordering },
                    ...sortableColumns.flatMap((column) => [
                      {
                        label: `${column.name} ASC`,
                        value: column.columnKey,
                        selected: requestData.ordering === column.columnKey,
                      },
                      {
                        label: `${column.name} DESC`,
                        value: `-${column.columnKey}`,
                        selected: requestData.ordering === `-${column.columnKey}`,
                      },
                    ]),
                  ]}
                  onSelect={({ value }) => handleSortSelect(value)}
                />
              )}
            </Top>
            {tableColumns.map((column, index) => (
              <ContainerDesktop key={index}>
                <Top hasSubcolumn={column.subcolumns !== null && active}>
                  {column.columnKey && dashboardUsersSorting ? (
                    <SortableHeader
                      label={column.name}
                      columnKey={column.columnKey}
                      direction={getColumnDirection(sortState, column.columnKey)}
                      onToggle={handleSortToggle}
                      tooltip={column.tooltip}
                    />
                  ) : (
                    <span>{column.name}</span>
                  )}
                  {active && column.subcolumns && (
                    <Subcolumn>
                      {column.subcolumns?.map((subcolumn, index) => (
                        <span key={index}>{subcolumn}</span>
                      ))}
                    </Subcolumn>
                  )}
                </Top>
              </ContainerDesktop>
            ))}
          </Row>

          {usersTable?.users?.length === 0 && <EmptyState />}
          {usersTable?.users?.map((row) => {
            return (
              <UserRow
                key={row.id}
                onSwitchManager={handleSwitchManager}
                onSwitchLicense={handleSwitchLicense}
                onSelectRow={handleCheckboxSelection}
                onCancelInvitation={handleRemoveUser}
                onResendInvitation={handleResendInvitation}
                onRemoveUser={handleRemoveUser}
                onSwitchAddon={handleSwitchAddon}
                onSwitchInviteAddon={skillDiveFlow ? handleSwitchInviteAddon : undefined}
                rowSelected={selecteds.includes(row.id)}
                active={usersTable?.active === 'true'}
                teams={null}
                onSelectTeam={null}
                row={row}
                tableGridConfig={rowTableConfig}
                ta
                showTeamSelectorButton={false}
                {...row}
              />
            );
          })}
        </Table>
      )}

      {fetching && usersTable && (
        <SpinerContainer absolute>
          <Spinner active />
        </SpinerContainer>
      )}

      {usersTable && (
        <Pagination
          count={usersTable?.count}
          page={requestData.page}
          page_size={requestData?.page_size}
          onPagesClick={handlePagination}
          rowsPerPageList={['6', '12', '18', '24']}
        />
      )}
      {modal}
    </CustomTableCard>
  );
};

export default memo(TeamsUsersTable);
